/**
 * CR-7743: "Days Left to Produce" on the PO Lines grid.
 *
 * Counts calendar days from today to the line's Latest Supplier Promise Date,
 * the date the supplier has committed to. Shown only while something is still
 * left to produce — a line whose outstanding quantity is all in the warehouse
 * or on the road has nothing to count down to, so the cell is blank.
 *
 * Red is critical: 15 days or less, and anything overdue. Amber up to 30,
 * green beyond — a quick check for staff training on the call-off board.
 */

/** At or below this many days (or overdue) the line is critical (red). */
export const DAYS_LEFT_CRITICAL = 15;
/** At or below this many days the line needs watching (amber). */
export const DAYS_LEFT_WARNING = 30;

export type DaysLeftTone = 'critical' | 'warning' | 'ok';

export interface DaysLeftToProduce {
  /** Signed calendar days to the promise date; negative once it has passed. */
  days: number;
  tone: DaysLeftTone;
  /** e.g. "12 days", "Due today", "3 days overdue". */
  label: string;
  /** Tailwind text + background classes for the badge. */
  className: string;
}

const TONE_CLASSES: Record<DaysLeftTone, string> = {
  critical: 'text-red-700 bg-red-50',
  warning: 'text-amber-700 bg-amber-50',
  ok: 'text-green-700 bg-green-50',
};

/** A YYYY-MM-DD date as local midnight, so no time zone shifts it a day. */
function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

function labelFor(days: number): string {
  if (days === 0) return 'Due today';
  if (days < 0) return `${-days} ${days === -1 ? 'day' : 'days'} overdue`;
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * Days left to produce a PO line, or null when there is nothing to show.
 *
 * @param promiseDate - the line's supplier_promise_date (YYYY-MM-DD)
 * @param remaining - the line's remaining_to_produce_qty
 * @param today - injected for tests; defaults to now
 * @returns the count, its tone and badge classes; null when nothing is left
 *   to produce, the remaining figure is unknown, or there is no promise date
 */
export function getDaysLeftToProduce(
  promiseDate: string | null | undefined,
  remaining: string | null | undefined,
  today: Date = new Date(),
): DaysLeftToProduce | null {
  if (remaining == null) return null;
  const left = Number(remaining);
  if (!Number.isFinite(left) || left <= 0) return null;
  if (!promiseDate) return null;
  const due = parseLocalDate(promiseDate);
  if (!due) return null;

  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due.getTime() - start.getTime()) / 86_400_000);
  let tone: DaysLeftTone = 'ok';
  if (days <= DAYS_LEFT_CRITICAL) tone = 'critical';
  else if (days <= DAYS_LEFT_WARNING) tone = 'warning';
  return { days, tone, label: labelFor(days), className: TONE_CLASSES[tone] };
}
